import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@redux/store'
import { ProductImage } from '@interfaces/product'
import Image from '@components/atoms/Image'
import { Swiper, SwiperSlide } from '@components/molecules/Slider'
import Modal from '@components/molecules/Modal'
import ImageCard from '@components/molecules/ImageCard'
import { truncateString } from '@helpers/global'

export interface ProductGalleryProps {
  productImages: ProductImage[]
}

const ProductGallery: React.FC<ProductGalleryProps> = ({ productImages }) => {
  const PDPVariant = useSelector((state: RootState) => state.PDP.PDPVariant)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [clickedImage, setClickedImage] = useState<ProductImage | null>(null)

  const openModal = (image: ProductImage) => {
    setClickedImage(image)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setClickedImage(null)
  }

  const getAltPart = (alt: string, partIndex: number): string => {
    if (!alt.includes('|')) return alt.trim()
    const parts = alt.split('|').map((part) => part.trim())
    return parts[partIndex - 1] || alt.trim()
  }

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isModalOpen])

  const filteredImages = PDPVariant?.isEyewear
    ? productImages.filter((img) => img.alt?.toLowerCase().includes(PDPVariant.title.toLowerCase()))?.length > 0
      ? productImages.filter((img) => {
          if (PDPVariant?.title && img.alt) {
            const part = img.alt.split('|')[0].trim()
            const cleanedPart = /^[^a-zA-Z]/.test(part[0]) ? part.slice(1).trim() : part
            return cleanedPart.toLowerCase() === PDPVariant.title.toLowerCase()
          }
          return false
        })
      : productImages.slice(0, 8)
    : productImages

  const modalImages = clickedImage
    ? [clickedImage, ...filteredImages.filter((img) => img.id !== clickedImage.id)]
    : filteredImages

  return (
    <div>
      <div className="block relative lg:hidden">
        <Swiper
          spaceBetween={0}
          slidesPerView={1}
          loop={true}
          pagination={{
            el: '.pdp-gallery-pagination',
            type: 'bullets',
            clickable: true,
            dynamicBullets: false,
          }}
        >
          {filteredImages.map((img: ProductImage, index) => (
            <SwiperSlide key={img.id} onClick={() => openModal(img)}>
              <ImageCard
                img={img}
                srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 }}
                badges={index === 0 ? PDPVariant?.metafields?.badges : undefined}
                imgTitle={truncateString(getAltPart(img.alt, 2), 30)}
                imgAlt={getAltPart(img.alt, 3)}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="pdp-gallery-pagination absolute z-10 text-center"></div>
      </div>

      <div className="hidden lg:flex flex-col gap-4">
        {filteredImages.map((img: ProductImage, index) => (
          <div key={img.id} className="w-full" onClick={() => openModal(img)}>
            <ImageCard
              img={img}
              srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 }}
              badges={index === 0 ? PDPVariant?.metafields?.badges : undefined}
              imgTitle={truncateString(getAltPart(img.alt, 2), 30)}
              imgAlt={getAltPart(img.alt, 3)}
            />
          </div>
        ))}
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <div className="relative rounded-b-lg pdp-gallery-modal mac-scroll cursor-zoom-out bg-white mt-5 max-w-3xl w-full overflow-y-auto max-h-[90vh] lg:max-w-screen-lg">
          <div className="flex flex-col gap-2">
            {modalImages.map((img: ProductImage) => (
              <div key={img.id} className="w-full">
                <Image {...img} srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 }} zoomable={true} />
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ProductGallery
